import { Box, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconUsers } from '@tabler/icons-react';
import { usePresenceManager } from '../../../hooks/usePresenceManager';
import Switch from '../../../components/ui/inputs/Switch';
import NumberInput from '../../../components/ui/inputs/NumberInput';

export default function PresenceSection() {
  const {
    config,
    updateConfig,
    lastHeartbeat,
    lastActivity,
    formattedIdleTime,
    isConnected,
  } = usePresenceManager();

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group gap="sm">
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconUsers size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600}>Presence Settings</Text>
          <Text size="xs" c="dimmed">
            Configure automatic presence features
          </Text>
        </Box>
      </Group>

      <Divider />

      {/* Connection Status */}
      <Group gap="xs">
        <Box
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: isConnected
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-color-gray-6)',
          }}
        />
        <Text size="sm" fw={500}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </Text>
      </Group>

      {/* Auto-Heartbeat */}
      <Stack gap="xs">
        <Switch
          label="Auto-Heartbeat"
          description="Automatically send heartbeat to keep presence active"
          checked={config.autoHeartbeat}
          onChange={(e) => updateConfig({ autoHeartbeat: e.currentTarget.checked })}
        />
        {config.autoHeartbeat && (
          <>
            <NumberInput
              label="Heartbeat Interval (seconds)"
              value={config.heartbeatInterval}
              onChange={(value) => updateConfig({ heartbeatInterval: Number(value) })}
              min={10}
              max={300}
              step={10}
              size="sm"
            />
            {lastHeartbeat && (
              <Text size="xs" c="dimmed">
                Last heartbeat: {lastHeartbeat.toLocaleTimeString()}
              </Text>
            )}
          </>
        )}
      </Stack>

      {/* Activity Tracking */}
      <Stack gap="xs">
        <Switch
          label="Activity Tracking"
          description="Track mouse and keyboard activity"
          checked={config.activityTracking}
          onChange={(e) => updateConfig({ activityTracking: e.currentTarget.checked })}
        />
        {config.activityTracking && (
          <>
            <NumberInput
              label="Activity Debounce (ms)"
              value={config.activityDebounce}
              onChange={(value) => updateConfig({ activityDebounce: Number(value) })}
              min={1000}
              max={30000}
              step={1000}
              size="sm"
            />
            {lastActivity && (
              <Text size="xs" c="dimmed">
                Last activity: {lastActivity.toLocaleTimeString()}
              </Text>
            )}
          </>
        )}
      </Stack>

      {/* Idle Detection */}
      <Stack gap="xs">
        <Switch
          label="Idle Detection"
          description="Automatically switch to Away after inactivity"
          checked={config.idleDetection}
          onChange={(e) => updateConfig({ idleDetection: e.currentTarget.checked })}
        />
        {config.idleDetection && (
          <>
            <NumberInput
              label="Idle Timeout (minutes)"
              description="Time of inactivity before you are marked as idle"
              value={config.idleTimeout}
              onChange={(value) => updateConfig({ idleTimeout: Number(value) })}
              min={1}
              max={60}
              step={1}
              size="sm"
            />
            <NumberInput
              label="Idle Grace Period (seconds)"
              description="Extra buffer time after timeout before idle takes effect"
              value={config.idleGracePeriod}
              onChange={(value) => updateConfig({ idleGracePeriod: Number(value) })}
              min={0}
              max={120}
              step={5}
              size="sm"
            />
            <Text size="xs" c="dimmed">
              Time until idle: {formattedIdleTime}
            </Text>
          </>
        )}
      </Stack>

      {/* Auto Lock on Idle */}
      <Stack gap="xs">
        <Switch
          label="Auto Lock on Idle"
          description="Automatically lock the screen when you become idle"
          checked={config.autoLockOnIdle}
          onChange={(e) => updateConfig({ autoLockOnIdle: e.currentTarget.checked })}
        />
        {config.autoLockOnIdle && (
          <NumberInput
            label="Lock Warning Time (seconds)"
            description="Countdown warning shown before the screen locks"
            value={config.lockWarningThreshold}
            onChange={(value) => updateConfig({ lockWarningThreshold: Number(value) })}
            min={5}
            max={60}
            step={5}
            size="sm"
          />
        )}
      </Stack>
    </Stack>
  );
}
